// istanbul ignore file
import type { FC, ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo } from 'react';

import type { SessionCustomer } from '@cld/session-service';
import { useCustomerIdContext } from '../customerId/CustomerId.context';

export interface ExtendedSessionCustomer extends SessionCustomer {
  status?: 'active' | 'disabled';
}

export const WorkspaceMenuContext = createContext<WorkspaceMenuContextProps | undefined>(undefined);

export const WorkspaceMenuContextProvider: FC<WorkspaceMenuContextProviderProps> = ({ getCustomersList, customersList, children }) => {
  const { currentCustomer, updateCurrentCustomer } = useCustomerIdContext();

  const setCurrentCustomer = useCallback(
    (id: string) => {
      const customer = getCustomerObjectById(customersList, id);
      if (customer) {
        updateCurrentCustomer(customer);
      }
    },
    [updateCurrentCustomer, customersList]
  );

  const memoizedContextValue = useMemo(
    () => ({
      getCustomersList,
      customersList,
      currentCustomerObject: currentCustomer,
      setCurrentCustomer,
    }),
    [getCustomersList, customersList, currentCustomer, setCurrentCustomer]
  );

  return <WorkspaceMenuContext.Provider value={memoizedContextValue}>{children}</WorkspaceMenuContext.Provider>;
};

export const useWorkspaceMenuContext = () => {
  const value = useContext(WorkspaceMenuContext);

  if (!value) {
    throw new Error('You need to wrap your component with WorkspaceMenuContextProvider');
  }

  return value;
};

export const getCustomerObjectById = (list: ExtendedSessionCustomer[], id: string | undefined) =>
  id ? list.find((customer) => customer.externalId === id) : undefined;

export interface WorkspaceMenuContextProviderProps {
  getCustomersList: (searchTerm?: string) => void;
  customersList: ExtendedSessionCustomer[];
  children?: ReactNode;
}

export interface WorkspaceMenuContextProps extends WorkspaceMenuContextProviderProps {
  currentCustomerObject?: ExtendedSessionCustomer;
  setCurrentCustomer: (id: string) => void;
}
